import { useEffect, useMemo, useRef, useState } from 'react';
import { Autosuggest, Toggle, SpaceBetween, StatusIndicator } from '@cloudscape-design/components';
import type { ApiClient } from '@deloitte-us-consulting/gp-sdk';
import { getCachedGraphAccessToken, getGraphAccessToken } from '../auth/getGraphAccessToken';
import { graphEntraConfigured } from '../auth/graphConfig';

/** The subset of a roster entry (GET /rbac/roster) this picker needs — kept
 *  narrow so every call site (RbacAdmin's App Access Lookup and Roles &
 *  Alliance Access tabs, AccessRequests' Impersonate tab) can pass their
 *  own already-loaded roster state without reshaping it. `status` (when
 *  present) is what lets the picker warn about someone who's never
 *  actually logged in yet — see `isPendingOnly` below. */
export interface PersonPickerEntry {
  email: string;
  name: string | null;
  roles?: { roleId: string; status?: 'active' | 'pending' }[];
  alliances?: { allianceName: string; status?: 'active' | 'pending' }[];
}

interface GraphPersonResult {
  id: string;
  displayName: string | null;
  mail: string | null;
  userPrincipalName: string;
}

/** True when every role/alliance this person has is still pending (queued,
 *  not yet applied) — meaning they've never actually logged in, have no
 *  real Cognito sub yet, and can't be impersonated in "specific individual"
 *  mode (there's no real access yet to preview) or targeted by a direct
 *  admin assignment (`resolveToSub` has nothing to resolve). Someone with
 *  zero roles/alliances at all isn't flagged — that just means no RBAC
 *  history, not that they're unresolvable. */
function isPendingOnly(p: PersonPickerEntry): boolean {
  const entries = [...(p.roles ?? []), ...(p.alliances ?? [])];
  return entries.length > 0 && entries.every((e) => e.status === 'pending');
}

const DEBOUNCE_MS = 350;

/**
 * Autocomplete against people already known to the platform (the roster —
 * both real, logged-in users and AirTable-sourced people still pending
 * their first login), instead of a bare free-text email/sub field. Suggests
 * existing matches first, tagged with their current roles/alliances so the
 * picker doubles as a quick "what do they already have" glance — but still
 * accepts free text for someone not in the roster at all (a brand new
 * Cognito user with no AirTable/RBAC history yet), so this never blocks on
 * the platform's own data being incomplete.
 *
 * Optionally (when `api` is passed and Entra is configured — ADR 0019) also
 * offers an opt-in tenant-wide search via Microsoft Graph, for someone who's
 * never touched the platform at all. That's a click-driven toggle, not
 * automatic: acquiring the Graph token may need a `signinPopup()`, which
 * only works from a synchronous user gesture. A Graph-only hit (no roster
 * entry) is tagged "Microsoft directory" and, like a pending roster entry,
 * "never logged in" — selecting it behaves exactly like typing a new email
 * does today; what happens next (an immediate grant vs. a queued
 * pending-bind) is decided server-side by whether it resolves to a real
 * Cognito sub, not by anything this component does.
 */
export function PersonAutosuggest({
  value,
  onChange,
  people,
  placeholder = 'Search by name or email…',
  api,
}: {
  value: string;
  onChange: (value: string, name?: string | null) => void;
  people: PersonPickerEntry[];
  placeholder?: string;
  /** Enables the optional Graph tenant-search toggle. Omit to keep this
   *  picker roster-only (e.g. impersonation, where a Graph-only hit has
   *  nothing to preview anyway). */
  api?: Pick<ApiClient, 'get'>;
}) {
  const [graphOn, setGraphOn] = useState(false);
  const [graphBusy, setGraphBusy] = useState(false);
  const [graphResults, setGraphResults] = useState<GraphPersonResult[]>([]);
  const [graphError, setGraphError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const graphAvailable = Boolean(api) && graphEntraConfigured;

  const rosterOptions = useMemo(
    () =>
      people.map((p) => {
        const pending = isPendingOnly(p);
        return {
          value: p.email,
          label: p.name || p.email,
          name: p.name,
          ...(p.name ? { description: p.email } : {}),
          ...(pending ? { labelTag: 'never logged in' } : {}),
          tags: [
            ...(pending ? ['⚠ pending — no real access to preview/target yet'] : []),
            ...(p.roles ?? []).map((r) => r.roleId),
            ...(p.alliances ?? []).map((a) => a.allianceName),
          ],
        };
      }),
    [people],
  );

  const rosterEmails = useMemo(() => new Set(people.map((p) => p.email.toLowerCase())), [people]);

  // Toggling on acquires the Graph token immediately, INSIDE the click
  // handler's own call stack — signinPopup() only works as a
  // synchronous-enough continuation of a real user gesture; by the time a
  // later debounced search runs, that gesture is long gone. Every
  // subsequent search below only reuses this cached token
  // (getCachedGraphAccessToken) and never tries to open a popup itself.
  const handleToggleGraph = async (checked: boolean) => {
    setGraphOn(checked);
    setGraphError(null);
    if (!checked) {
      setGraphResults([]);
      return;
    }
    try {
      await getGraphAccessToken();
    } catch (e) {
      setGraphError((e as Error).message);
      setGraphOn(false);
    }
  };

  // Graph search — debounced, and only ever run while the toggle is on.
  useEffect(() => {
    if (!graphOn || !api) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = value.trim();
    if (query.length < 2) {
      setGraphResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setGraphBusy(true);
      try {
        const token = await getCachedGraphAccessToken();
        const results = await api.get<GraphPersonResult[]>(`/api/rbac/people-search?query=${encodeURIComponent(query)}`, {
          'x-graph-token': token,
        });
        setGraphResults(results);
        setGraphError(null);
      } catch (e) {
        setGraphError((e as Error).message);
        setGraphResults([]);
      } finally {
        setGraphBusy(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [graphOn, value, api]);

  const graphOptions = useMemo(
    () =>
      graphResults
        .filter((g) => !rosterEmails.has((g.mail ?? g.userPrincipalName).toLowerCase()))
        .map((g) => ({
          value: g.mail ?? g.userPrincipalName,
          label: g.displayName || g.userPrincipalName,
          name: g.displayName,
          description: g.mail ?? g.userPrincipalName,
          labelTag: 'never logged in',
          tags: ['Microsoft directory', '⚠ pending — no real access to preview/target yet'],
        })),
    [graphResults, rosterEmails],
  );

  // Always filteringType="manual" — this component owns matching against
  // `value` itself (a plain case-insensitive substring test over the same
  // fields Cloudscape's "auto" mode searches: name, email, role/alliance
  // tags). Two reasons: "auto" has no idea about the async Graph results
  // merged in below, and "auto" deliberately shows EVERY option when the
  // input is re-focused with text already in it (until the next keystroke),
  // so clicking back into a field holding "senawi" listed the whole roster.
  const needle = value.trim().toLowerCase();
  const filteredRoster = !needle
    ? rosterOptions
    : rosterOptions.filter(
        (o) =>
          o.label.toLowerCase().includes(needle) ||
          o.description?.toLowerCase().includes(needle) ||
          o.tags.some((t) => t.toLowerCase().includes(needle)),
      );
  const options = graphOn ? [...filteredRoster, ...graphOptions] : filteredRoster;

  const handleChange = (v: string) => {
    const match = options.find((o) => o.value === v);
    onChange(v, match?.name ?? null);
  };

  return (
    <SpaceBetween size="xs">
      <Autosuggest
        value={value}
        onChange={(e) => handleChange(e.detail.value)}
        options={options}
        placeholder={placeholder}
        filteringType="manual"
        statusType={graphOn && graphBusy ? 'loading' : 'finished'}
        loadingText="Searching the Microsoft directory…"
        enteredTextLabel={(v) => `Use "${v}" — not found in the platform yet`}
        empty="No matches — you can still type an email directly for someone new."
      />
      {graphAvailable && (
        <Toggle checked={graphOn} onChange={(e) => void handleToggleGraph(e.detail.checked)}>
          Also search the Microsoft directory (may prompt a one-time sign-in popup)
        </Toggle>
      )}
      {graphError && <StatusIndicator type="error">{graphError}</StatusIndicator>}
    </SpaceBetween>
  );
}
